import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, TYPE_STYLES, STATUS_LABELS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import EditModal from "@/components/EditModal";
import DeleteConfirm from "@/components/DeleteConfirm";
import {
  ArrowLeft,
  ExternalLink,
  Pencil,
  Trash2,
  RefreshCw,
  Loader2,
  AlertCircle,
  Link as LinkIcon,
  Calendar,
  User,
  FileText,
  FileDown,
  Copy,
} from "lucide-react";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("it-IT", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

export default function DetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [resource, setResource] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const fetch = useCallback(async () => {
    try {
      const r = await api.get(id);
      setResource(r);
    } catch (e) {
      if (e?.response?.status === 404) setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  // Poll while processing
  useEffect(() => {
    if (!resource) return;
    if (!["pending", "extracting", "distilling"].includes(resource.status)) return;
    const iv = setInterval(fetch, 2500);
    return () => clearInterval(iv);
  }, [resource, fetch]);

  const handleSave = async (form) => {
    try {
      const updated = await api.update(id, form);
      setResource(updated);
      toast.success("Scheda aggiornata");
    } catch (e) {
      toast.error("Aggiornamento fallito", {
        description: e?.response?.data?.detail || String(e),
      });
    }
  };

  const handleDelete = async () => {
    try {
      await api.remove(id);
      toast.success("Scheda eliminata");
      navigate("/library");
    } catch (e) {
      toast.error("Eliminazione fallita");
    }
  };

  const handleReprocess = async () => {
    try {
      const r = await api.reprocess(id);
      setResource(r);
      toast.success("Ri-elaborazione avviata");
    } catch (e) {
      toast.error("Operazione fallita");
    }
  };

  const handleExport = async (fmt) => {
    try {
      const base = process.env.REACT_APP_BACKEND_URL || "";
      const url = `${base}/api/resources/${id}/export?format=${fmt}`;
      const resp = await fetch(url);
      if (!resp.ok) throw new Error("Export fallito");
      const blob = await resp.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      const ext = fmt === "pdf" ? "pdf" : "md";
      const safe =
        (resource?.title || "scheda")
          .toLowerCase()
          .replace(/[^\w\s-]/g, "")
          .replace(/\s+/g, "-")
          .slice(0, 60) || "scheda";
      a.download = `${safe}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);
      toast.success(`Scaricato ${ext.toUpperCase()}`);
    } catch (e) {
      toast.error("Export fallito", { description: String(e) });
    }
  };

  const handleCopyMarkdown = async () => {
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/resources/${id}/export?format=md`;
      const resp = await fetch(url);
      if (!resp.ok) throw new Error("Fetch markdown fallito");
      const text = await resp.text();
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for non-secure contexts
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        ta.remove();
      }
      toast.success("Markdown copiato negli appunti");
    } catch (e) {
      toast.error("Copia fallita", { description: String(e) });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32 text-zinc-400">
        <Loader2 className="animate-spin" size={24} />
      </div>
    );
  }

  if (notFound || !resource) {
    return (
      <section className="max-w-2xl mx-auto px-6 py-24 text-center">
        <h1 className="font-display text-3xl mb-4">Scheda non trovata</h1>
        <p className="text-zinc-500 mb-8">
          La risorsa che stai cercando non esiste o è stata eliminata.
        </p>
        <Link
          to="/library"
          className="inline-flex items-center gap-2 text-sm text-[#002FA7] hover:underline"
        >
          <ArrowLeft size={14} /> Torna alla libreria
        </Link>
      </section>
    );
  }

  const style = TYPE_STYLES[resource.resource_type] || TYPE_STYLES.web_page;
  const isProcessing = ["pending", "extracting", "distilling"].includes(
    resource.status,
  );
  const isFailed = resource.status === "failed";

  return (
    <>
      <article
        className="max-w-3xl mx-auto px-6 py-12"
        data-testid="detail-page"
      >
        <Link
          to="/library"
          data-testid="detail-back"
          className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-900 mb-8 transition-colors"
        >
          <ArrowLeft size={14} /> Libreria
        </Link>

        <div className="flex flex-wrap items-center gap-3 mb-6">
          <span
            className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold border ${style.bg} ${style.text} ${style.border}`}
          >
            {style.label}
          </span>
          {isProcessing && (
            <span
              data-testid="detail-processing"
              className="inline-flex items-center gap-2 text-xs font-medium text-zinc-600 bg-zinc-100 rounded-full px-3 py-1"
            >
              <Loader2 size={12} className="animate-spin" />
              {STATUS_LABELS[resource.status]}
            </span>
          )}
          {isFailed && (
            <span className="inline-flex items-center gap-2 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-full px-3 py-1">
              <AlertCircle size={12} /> Fallito
            </span>
          )}
        </div>

        <h1 className="font-display text-4xl sm:text-5xl tracking-tighter font-medium mb-4 text-balance">
          {resource.title || resource.source_url}
        </h1>

        {resource.subtitle && (
          <p className="text-lg text-zinc-600 leading-relaxed mb-8">
            {resource.subtitle}
          </p>
        )}

        <div className="flex flex-wrap items-center gap-5 text-sm text-zinc-500 pb-8 mb-8 border-b border-zinc-200">
          <span className="inline-flex items-center gap-1.5">
            <Calendar size={14} /> {formatDate(resource.created_at)}
          </span>
          {resource.author && (
            <span className="inline-flex items-center gap-1.5">
              <User size={14} /> {resource.author}
            </span>
          )}
          <a
            href={resource.source_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="detail-source-link"
            className="inline-flex items-center gap-1.5 text-[#002FA7] hover:underline truncate max-w-xs"
          >
            <ExternalLink size={14} /> Apri risorsa
          </a>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2 mb-12">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setEditOpen(true)}
            data-testid="detail-edit"
            disabled={isProcessing}
          >
            <Pencil size={14} className="mr-1.5" />
            Modifica
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleReprocess}
            data-testid="detail-reprocess"
            disabled={isProcessing}
          >
            <RefreshCw size={14} className="mr-1.5" />
            Ri-elabora
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleCopyMarkdown}
            data-testid="detail-copy-md"
            disabled={isProcessing || resource.status !== "ready"}
          >
            <Copy size={14} className="mr-1.5" />
            Copia MD
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleExport("md")}
            data-testid="detail-export-md"
            disabled={isProcessing || resource.status !== "ready"}
          >
            <FileText size={14} className="mr-1.5" />
            Markdown
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleExport("pdf")}
            data-testid="detail-export-pdf"
            disabled={isProcessing || resource.status !== "ready"}
          >
            <FileDown size={14} className="mr-1.5" />
            PDF
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setDeleteOpen(true)}
            data-testid="detail-delete"
            className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
          >
            <Trash2 size={14} className="mr-1.5" />
            Elimina
          </Button>
        </div>

        {isFailed && (
          <div className="border border-red-200 bg-red-50 rounded-xl p-6 mb-10">
            <h3 className="font-display text-lg text-red-900 mb-2 flex items-center gap-2">
              <AlertCircle size={18} /> Elaborazione fallita
            </h3>
            <p className="text-sm text-red-800 leading-relaxed">
              {resource.error || "Errore sconosciuto."}
            </p>
            <p className="text-xs text-red-700 mt-3">
              Puoi ritentare usando "Ri-elabora" oppure modificare manualmente i
              campi.
            </p>
          </div>
        )}

        {isProcessing && (
          <div className="border border-zinc-200 bg-zinc-50 rounded-xl p-10 text-center mb-10">
            <Loader2
              className="animate-spin mx-auto mb-4 text-zinc-400"
              size={28}
            />
            <h3 className="font-display text-lg mb-1">
              Elaborazione in corso…
            </h3>
            <p className="text-sm text-zinc-500">
              {STATUS_LABELS[resource.status]} · Questa pagina si aggiorna da
              sola.
            </p>
          </div>
        )}

        {resource.status === "ready" && (
          <div className="flex flex-col gap-12">
            {/* Insight */}
            {resource.insight_paragraph && (
              <section>
                <h2 className="text-xs font-semibold tracking-[0.2em] uppercase text-zinc-500 mb-4">
                  Panoramica
                </h2>
                <p className="text-lg leading-relaxed text-zinc-800">
                  {resource.insight_paragraph}
                </p>
              </section>
            )}

            {/* Keywords */}
            {resource.keywords && resource.keywords.length > 0 && (
              <section>
                <h2 className="text-xs font-semibold tracking-[0.2em] uppercase text-zinc-500 mb-4">
                  Keywords
                </h2>
                <div className="flex flex-wrap gap-2">
                  {resource.keywords.map((kw) => (
                    <Badge
                      key={kw}
                      className="bg-zinc-900 text-white font-normal"
                    >
                      {kw}
                    </Badge>
                  ))}
                </div>
              </section>
            )}

            {/* Key points */}
            {resource.key_points && resource.key_points.length > 0 && (
              <section>
                <h2 className="text-xs font-semibold tracking-[0.2em] uppercase text-zinc-500 mb-4">
                  Punti chiave
                </h2>
                <ul className="flex flex-col gap-5">
                  {resource.key_points.map((kp, i) => (
                    <li
                      key={i}
                      className="flex gap-4 border-l-2 border-[#002FA7] pl-5"
                    >
                      <div className="flex-1">
                        <h3 className="font-display text-lg font-medium mb-1">
                          {kp.point}
                        </h3>
                        {kp.summary && (
                          <p className="text-zinc-600 leading-relaxed">
                            {kp.summary}
                          </p>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Actionable steps */}
            {resource.actionable_steps && resource.actionable_steps.length > 0 && (
              <section>
                <h2 className="text-xs font-semibold tracking-[0.2em] uppercase text-zinc-500 mb-4">
                  Step operativi
                </h2>
                <ol className="flex flex-col gap-3 counter-reset-steps">
                  {resource.actionable_steps.map((s, i) => (
                    <li
                      key={i}
                      className="flex gap-4 items-start p-4 bg-zinc-50 rounded-lg border border-zinc-200"
                    >
                      <span
                        className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold text-white font-mono"
                        style={{ backgroundColor: "#002FA7" }}
                      >
                        {i + 1}
                      </span>
                      <span className="text-zinc-800 leading-relaxed pt-0.5">
                        {s}
                      </span>
                    </li>
                  ))}
                </ol>
              </section>
            )}

            {/* References */}
            {resource.references && resource.references.length > 0 && (
              <section>
                <h2 className="text-xs font-semibold tracking-[0.2em] uppercase text-zinc-500 mb-4">
                  Riferimenti
                </h2>
                <ul className="flex flex-col gap-2">
                  {resource.references.map((ref, i) => (
                    <li key={i}>
                      <a
                        href={ref.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group inline-flex items-start gap-2 p-3 rounded-lg hover:bg-zinc-50 border border-transparent hover:border-zinc-200 transition-all w-full"
                      >
                        <LinkIcon
                          size={14}
                          className="mt-1 text-zinc-400 flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm group-hover:text-[#002FA7] truncate">
                            {ref.label || ref.url}
                          </div>
                          <div className="text-xs text-zinc-500 truncate">
                            {ref.url}
                          </div>
                        </div>
                        <Badge
                          variant="outline"
                          className="text-[10px] font-mono"
                        >
                          {ref.type}
                        </Badge>
                      </a>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        )}
      </article>

      <EditModal
        open={editOpen}
        onOpenChange={setEditOpen}
        resource={resource}
        onSave={handleSave}
      />
      <DeleteConfirm
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        onConfirm={handleDelete}
        title={resource.title}
      />
    </>
  );
}
